import { createClient, type EmailOtpType } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

const supabase_url = import.meta.env.VITE_SUPABASE_URL;
const supabase_anon_key = import.meta.env.VITE_SUPABASE_ANON_KEY;

export type RecoverySessionResult =
  | { status: 'ready' }
  | { status: 'invalid_link' }
  | { status: 'error'; message: string };

type AuthErrorLike = {
  message?: string | null;
  code?: string | null;
  name?: string | null;
};

function cleanAuthParamsFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('code');
  url.searchParams.delete('token_hash');
  url.searchParams.delete('type');
  url.searchParams.delete('error');
  url.searchParams.delete('error_code');
  url.searchParams.delete('error_description');
  url.hash = '';
  window.history.replaceState({}, '', `${url.pathname}${url.search}`);
}

function getUrlAuthError(): { error: string; errorCode: string } | null {
  const url = new URL(window.location.href);
  const hashParams = new URLSearchParams(url.hash.startsWith('#') ? url.hash.slice(1) : '');
  const error = url.searchParams.get('error') ?? hashParams.get('error');
  const errorCode = url.searchParams.get('error_code') ?? hashParams.get('error_code');
  if (error || errorCode) {
    return { error: error ?? '', errorCode: errorCode ?? '' };
  }
  return null;
}

export function isExpiredOrInvalidRecoveryError(error: AuthErrorLike | null | undefined): boolean {
  if (!error) return false;
  const code = (error.code ?? '').toLowerCase();
  const message = (error.message ?? '').toLowerCase();
  if (
    code === 'otp_expired' ||
    code === 'access_denied' ||
    code === 'flow_state_expired' ||
    code === 'otp_disabled' ||
    code === 'pkce_failed'
  ) {
    return true;
  }
  if (message.includes('expired')) return true;
  if (message.includes('pkce') || message.includes('code verifier')) return true;
  if (
    message.includes('invalid') &&
    (message.includes('token') || message.includes('otp') || message.includes('link'))
  ) {
    return true;
  }
  return false;
}

export function isUnusableRecoverySessionError(error: unknown): boolean {
  const authError = error as AuthErrorLike;
  const message = (authError?.message ?? '').toLowerCase();
  const name = (authError?.name ?? '').toLowerCase();
  if (name.includes('authsessionmissing') || message.includes('session missing')) {
    return true;
  }
  return isExpiredOrInvalidRecoveryError(authError);
}

function recoveryResultFromAuthError(error: AuthErrorLike): RecoverySessionResult {
  if (isExpiredOrInvalidRecoveryError(error)) {
    return { status: 'invalid_link' };
  }
  return { status: 'error', message: error.message || 'Auth error' };
}

/**
 * Request a recovery email without PKCE.
 *
 * `@supabase/ssr` uses PKCE by default, which stores a code verifier in the
 * initiating browser. Opening the email on another device then fails with
 * "PKCE code verifier not found in storage". Implicit flow (or a token_hash
 * email template + verifyOtp) works cross-browser.
 */
export async function requestPasswordRecovery(email: string): Promise<void> {
  const client = createClient(supabase_url, supabase_anon_key, {
    auth: {
      flowType: 'implicit',
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
  });
  const { error } = await client.auth.resetPasswordForEmail(email.trim(), {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) throw error;
}

/**
 * Establishes a Supabase session from password-recovery URL parameters.
 *
 * After migrating to @supabase/ssr (PKCE + cookies), implicit-flow tokens in the
 * URL hash and token_hash query params are no longer picked up automatically.
 * Admin-generated recovery links and default Supabase email redirects use those
 * formats, so we exchange them explicitly before calling updateUser().
 *
 * Expired/invalid redirects from GoTrue arrive as `error` / `error_code=otp_expired`
 * in the query or hash and must not fall back to a leftover cookie session.
 */
export async function establishRecoverySession(): Promise<RecoverySessionResult> {
  if (getUrlAuthError()) {
    cleanAuthParamsFromUrl();
    return { status: 'invalid_link' };
  }

  const url = new URL(window.location.href);
  const params = url.searchParams;
  const token_hash = params.get('token_hash');
  const type = params.get('type');
  const code = params.get('code');

  if (token_hash && type) {
    const { error } = await supabase.auth.verifyOtp({
      token_hash,
      type: type as EmailOtpType,
    });
    cleanAuthParamsFromUrl();
    if (error) return recoveryResultFromAuthError(error);
    return { status: 'ready' };
  }

  if (code) {
    const { error } = await supabase.auth.exchangeCodeForSession(code);
    cleanAuthParamsFromUrl();
    if (error) return recoveryResultFromAuthError(error);
    return { status: 'ready' };
  }

  const hash = window.location.hash.substring(1);
  if (hash) {
    const hashParams = new URLSearchParams(hash);
    const access_token = hashParams.get('access_token');
    const refresh_token = hashParams.get('refresh_token');
    if (access_token && refresh_token) {
      const { error } = await supabase.auth.setSession({ access_token, refresh_token });
      cleanAuthParamsFromUrl();
      if (error) return recoveryResultFromAuthError(error);
      return { status: 'ready' };
    }
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (session) return { status: 'ready' };

  return { status: 'invalid_link' };
}
